import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/lib/notify";
import API from "@/lib/api";
import {
  Upload, FileText, Loader2, X, Plus, Trash2, Save, ArrowLeft, Sparkles, CheckCircle2, Network,
} from "lucide-react";
import InvestorTypeSelector from "@/components/InvestorTypeSelector";
import { useIdempotentSubmit } from "@/hooks/useIdempotentSubmit";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "@/components/UnsavedChangesDialog";

const FIELD_TYPES = [
  { value: "text", label: "Text" }, { value: "number", label: "Number" },
  { value: "email", label: "Email" }, { value: "phone", label: "Phone" },
  { value: "date", label: "Date" }, { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
];

const FUND_TYPES = ["Hedge Fund", "Digital Asset Fund", "Private Equity", "Venture Capital", "Real Estate Fund", "Fund of Funds", "Other"];
const OFFERING_TYPES = [{ value: "shares", label: "Shares" }, { value: "units", label: "Units" }, { value: "interest", label: "Interest" }];
const STATEMENT_TEMPLATES = [
  { value: "template_1", label: "Template 1 - NAV Statement", desc: "Account Summary (MTD/YTD), Shares Summary, Closing NAV" },
  { value: "template_2", label: "Template 2 - Equity Statement", desc: "Capital Additions/Withdrawals, Fee Allocations, Net Return" },
  { value: "template_3", label: "Template 3 - Partner Capital", desc: "Capital Commitment, MOIC, Operating Activity (QTD/YTD/Inception)" },
];

const OPEN_ENDED_TYPES = ["hedge fund", "digital asset fund", "digital asset", "crypto"];
const CLOSED_ENDED_TYPES = ["private equity", "venture capital", "real estate fund"];

function detectCategory(fundType) {
  const t = (fundType || "").toLowerCase();
  for (const ct of CLOSED_ENDED_TYPES) { if (t.includes(ct)) return "closed_ended"; }
  for (const ot of OPEN_ENDED_TYPES) { if (t.includes(ot)) return "open_ended"; }
  return "open_ended";
}

const emptyOffering = () => ({
  id: crypto.randomUUID(), name: "", type: "shares",
  management_fee: "", mf_logic: "", performance_fee: "", pf_logic: "",
  hurdle_rate: "", hurdle_logic: "", high_watermark: "", loss_carry_forward: "", clawback: "", others: "",
});

export default function CreateFund() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [fundId, setFundId] = useState(null);
  // TY: origin of the fundId row — determines Discard behavior.
  //   "ppm"    → row was created by PPM upload; Discard deletes it.
  //   "manual" → in-memory only until final Save; Discard deletes nothing.
  //   null     → no fundId yet.
  const [fundOrigin, setFundOrigin] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  // Existing fund names for the selected org — used only for the
  // duplicate-name UI guard (case-insensitive). Fetch failures fail-open.
  const [existingFundNames, setExistingFundNames] = useState([]);
  // The name of the fund row currently being edited (PPM flow) — lets the
  // duplicate-name guard skip the fund's own existing name. Never sent to
  // the API.
  const [originalFundName, setOriginalFundName] = useState("");
  // Duplicate-submit guards. `useIdempotentSubmit` locks the button and
  // stamps an Idempotency-Key header on the create request; a same-key
  // retry on the backend replays the first result instead of creating a
  // second fund.
  const { submitting: processing, run: runProcessPpm } = useIdempotentSubmit();
  // Manual entry no longer creates a DB row on click — kept as a
  // placeholder so we don't ripple across the render below. Guard the
  // eslint no-unused-vars via the void marker.
  const { submitting: creatingManual } = useIdempotentSubmit();
  void creatingManual;
  // TASK 1: dirty tracker + unsaved-changes guard. Any user edit flips
  // `dirty=true`; the guard intercepts browser refresh (beforeunload),
  // the browser Back button (popstate), and the in-page Back arrow
  // (guardedNavigate). Router-agnostic — no react-router data-router
  // dependency, so it never crashes the initial render.
  // refresh (beforeunload) so operators can Save-as-Draft / Discard /
  // Cancel before they lose in-progress work. Note: `dirty` is scoped to
  // manual entry only — a PPM row is already persisted by the backend
  // so the confirm dialog also fires when it's just been created.
  const [dirty, setDirty] = useState(false);
  // Required-field inline errors (fund-create form). Keys match the five
  // GL-required fields: name / currency / onboardingMode / incorp_date /
  // reportingStartDate. Empty string = no error.
  const [fieldErrors, setFieldErrors] = useState({
    name: "", currency: "", onboardingMode: "", incorp_date: "", reportingStartDate: "",
  });

  const isValidDate = (v) => {
    if (!v) return false;
    // HTML date inputs give YYYY-MM-DD; reject anything that doesn't parse.
    const d = new Date(v);
    return !Number.isNaN(d.getTime());
  };

  const validateRequiredFields = () => {
    const trimmedName = info.name.trim();
    const isDuplicate =
      trimmedName &&
      // Skip the check for the row we're editing (PPM flow) — its own
      // name must not collide with itself.
      !(originalFundName && trimmedName.toLowerCase() === originalFundName.trim().toLowerCase()) &&
      existingFundNames.includes(trimmedName.toLowerCase());
    const errs = {
      name: !trimmedName
        ? "Fund name is required"
        : isDuplicate
          ? "A fund with this name already exists in this organization"
          : "",
      currency: (info.currency || "").trim() ? "" : "Reporting currency is required",
      onboardingMode: ["new", "existing"].includes(onboardingMode) ? "" : "Select New Fund or Existing Fund",
      incorp_date: isValidDate(info.incorp_date) ? "" : "A valid incorporation date is required",
      reportingStartDate: isValidDate(reportingStartDate) ? "" : "A valid reporting start date is required",
    };
    setFieldErrors(errs);
    return !errs.name && !errs.currency && !errs.onboardingMode && !errs.incorp_date && !errs.reportingStartDate;
  };

  const clearFieldError = (key) => {
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  useEffect(() => {
    API.get("/organizations").then(({ data }) => setOrgs(data || [])).catch(() => {});
  }, []);

  // Load existing fund names for the selected org so the duplicate-name
  // guard can check locally before any save attempt. Fail-open on error.
  useEffect(() => {
    if (!selectedOrgId) { setExistingFundNames([]); return; }
    let cancelled = false;
    API.get(`/funds?org_id=${encodeURIComponent(selectedOrgId)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setExistingFundNames(
          (data || [])
            .map((f) => (f.name || "").trim().toLowerCase())
            .filter(Boolean)
        );
      })
      .catch(() => { if (!cancelled) setExistingFundNames([]); });
    return () => { cancelled = true; };
  }, [selectedOrgId]);

  const [info, setInfo] = useState({
    name: "", fund_type: "", strategy: "", terms: "", investment_minimum: "",
    currency: "USD",
    fund_description: "", fund_address: "", incorp_date: "",
  });
  const [reportingFreq, setReportingFreq] = useState("");
  const [reportingStartDate, setReportingStartDate] = useState("");
  const [fundCategory, setFundCategory] = useState("open_ended");
  const [statementTemplate, setStatementTemplate] = useState("template_1");
  const [offerings, setOfferings] = useState([]);
  const [sharesApplicable, setSharesApplicable] = useState(false);
  const [fields, setFields] = useState([]);
  const [docs, setDocs] = useState([]);
  // Extended fund profile state
  const [decimalPrecision, setDecimalPrecision] = useState(2);
  const [fyStartsOn, setFyStartsOn] = useState("");
  const [fyEndsOn, setFyEndsOn] = useState("");
  const [reportingMtd, setReportingMtd] = useState(true);
  const [reportingQtd, setReportingQtd] = useState(true);
  const [reportingYtd, setReportingYtd] = useState(true);
  const [reportingItd, setReportingItd] = useState(true);
  const [commissionAccountingMethod, setCommissionAccountingMethod] = useState("");
  const [dateFormat, setDateFormat] = useState("YYYY-MM-DD");
  const [timezone, setTimezone] = useState("UTC");
  const [onboardingMode, setOnboardingMode] = useState("");
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(false);
  const [fxCostMethod, setFxCostMethod] = useState("");

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer?.files || []);
    const pdfs = dropped.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length === 0) { toast.error("Please upload PDF files"); return; }
    setFiles((prev) => [...prev, ...pdfs]);
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const pdfs = selected.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length > 0) setFiles((prev) => [...prev, ...pdfs]);
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  // Populate the whole create-fund form from a parsed fund document.
  const applyParsedFund = (data) => {
    setFundId(data.id);
    setFundOrigin("ppm");
    setOriginalFundName(data.name || "");
    // PPM upload creates a persisted stub — treat it as dirty so leaving
    // without an explicit Save triggers the confirm modal.
    setDirty(true);
    setInfo({
      name: data.name || "", fund_type: data.fund_type || "", strategy: data.strategy || "",
      terms: data.terms || "", investment_minimum: data.investment_minimum || "",
      currency: data.currency || "USD",
      fund_description: data.fund_description || "",
      fund_address: data.fund_address || "",
      incorp_date: data.incorp_date || "",
    });
    setReportingFreq(data.reporting_frequency || "");
    setReportingStartDate(data.reporting_start_date || "");
    setFundCategory(data.fund_category || detectCategory(data.fund_type));
    setOfferings(data.offerings || []);
    setStatementTemplate(data.statement_template || "template_1");
    setSharesApplicable(data.shares_applicable || false);
    setFields(data.onboarding_workflow?.required_fields || []);
    setDocs(data.onboarding_workflow?.required_documents || []);
    setDecimalPrecision(data.decimal_precision ?? 2);
    setFyStartsOn(data.fy_starts_on || "");
    setFyEndsOn(data.fy_ends_on || "");
    setReportingMtd(data.reporting_mtd ?? true);
    setReportingQtd(data.reporting_qtd ?? true);
    setReportingYtd(data.reporting_ytd ?? true);
    setReportingItd(data.reporting_itd ?? true);
    setCommissionAccountingMethod(data.commission_accounting_method || "");
    setDateFormat(data.date_format || "YYYY-MM-DD");
    setTimezone(data.timezone || "UTC");
    setOnboardingMode(data.onboarding_mode || "");
    setMultiCurrencyEnabled(data.multi_currency_enabled ?? false);
    setFxCostMethod(data.fx_cost_method || "");
    // Surface AI parse status so operators don't get a silent "New Fund"
    // fallback. `complete` is the happy path; `partial` / `failed` warn them
    // to review the form before saving.
    const status = data.ai_parse_status;
    if (status === "failed") {
      toast.error(
        data.ai_parse_error
          ? `PPM parsing failed — defaults loaded. Reason: ${data.ai_parse_error}`
          : "PPM parsing failed — defaults loaded. Please review every section before saving.",
        { duration: 12000 }
      );
    } else if (status === "partial") {
      toast.warning(
        "Only part of the PPM could be parsed — some sections were filled from defaults. Please review the form before saving.",
        { duration: 10000 }
      );
    } else {
      toast.success("PPM analyzed successfully");
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    if (!selectedOrgId) { toast.error("Please select an organization first"); return; }
    await runProcessPpm(async (idempotencyKey) => {
      try {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        formData.append("org_id", selectedOrgId);
        // The backend returns immediately with an `ai_parse_status: "processing"`
        // stub and parses in the background — so a long parse can't trip the
        // gateway timeout. Poll until the fund finishes parsing.
        const { data } = await API.post("/funds/upload-ppm", formData, {
          headers: { "Idempotency-Key": idempotencyKey },
        });
        if (data.ai_parse_status === "processing") {
          setFundId(data.id);
          const finished = await pollUntilParsed(data.id);
          applyParsedFund(finished);
        } else {
          applyParsedFund(data);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to process PPM");
      }
    });
  };

  // Poll GET /funds/{id} until ai_parse_status moves off "processing".
  // ~4 minute ceiling (80 × 3s) covers even the largest multi-file parses.
  const pollUntilParsed = async (id) => {
    for (let i = 0; i < 80; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await API.get(`/funds/${id}`);
        if (data.ai_parse_status && data.ai_parse_status !== "processing") {
          return data;
        }
      } catch (e) {
        // transient error while polling — keep trying
      }
    }
    // Timed out waiting; return what we have so the operator can still edit.
    const { data } = await API.get(`/funds/${id}`);
    return data;
  };


  const handleStartManual = async () => {
    if (!selectedOrgId) { toast.error("Please select an organization first"); return; }
    // TASK 1: DO NOT create a DB row on Start Manual. Open the form in
    // memory; the row is only persisted when the user clicks Save. This
    // keeps orphan/empty drafts out of the database.
    setManualMode(true);
    setFundOrigin("manual");
    setFundId(null);
    setOriginalFundName("");
    setInfo({
      name: "", fund_type: "", strategy: "", terms: "", investment_minimum: "",
      currency: "USD", fund_description: "", fund_address: "", incorp_date: "",
    });
    setReportingFreq("");
    setReportingStartDate("");
    setFundCategory("open_ended");
    setOfferings([]);
    setStatementTemplate("template_1");
    setSharesApplicable(false);
    setFields([]);
    setDocs([]);
    setDecimalPrecision(2);
    setFyStartsOn(""); setFyEndsOn("");
    setReportingMtd(true); setReportingQtd(true); setReportingYtd(true); setReportingItd(true);
    setCommissionAccountingMethod("");
    setDateFormat("YYYY-MM-DD");
    setTimezone("UTC");
    setOnboardingMode("");
    setMultiCurrencyEnabled(false);
    setFxCostMethod("");
    setDirty(false);
    toast.success("Fill in the details below and click Save when ready");
  };

  const handleFundTypeChange = (val) => {
    setInfo({ ...info, fund_type: val });
    setFundCategory(detectCategory(val));
  };

  const addOffering = () => setOfferings(prev => [...prev, emptyOffering()]);
  const removeOffering = (idx) => setOfferings(prev => prev.filter((_, i) => i !== idx));
  const updateOffering = (idx, key, val) => setOfferings(prev => prev.map((o, i) => i === idx ? { ...o, [key]: val } : o));

  const updateField = (idx, key, value) => setFields(prev => prev.map((f, i) => (i === idx ? { ...f, [key]: value } : f)));
  const addField = () => setFields(prev => [...prev, { id: crypto.randomUUID(), field_name: "", field_type: "text", required: true }]);
  const removeField = (idx) => setFields(prev => prev.filter((_, i) => i !== idx));
  const updateDoc = (idx, key, value) => setDocs(prev => prev.map((d, i) => (i === idx ? { ...d, [key]: value } : d)));
  const addDoc = () => setDocs(prev => [...prev, { id: crypto.randomUUID(), doc_name: "", description: "", required: true }]);
  const removeDoc = (idx) => setDocs(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async (activate = false) => {
    // Block submission until ALL required fields are filled and valid —
    // inline red errors shown in one pass (matches the org-form pattern).
    if (!validateRequiredFields()) return;
    if (!reportingFreq) { toast.error("Reporting frequency is required"); return; }
    setSaving(true);
    try {
      const classes = offerings.map(o => o.name).filter(Boolean);
      const payload = {
        ...info,
        investment_minimum: parseFloat(info.investment_minimum) || 0,
        reporting_frequency: reportingFreq,
        reporting_start_date: reportingStartDate || null,
        fund_category: fundCategory,
        class_type: offerings.length > 1 ? "multi" : "single",
        classes,
        offerings,
        shares_applicable: sharesApplicable,
        statement_template: statementTemplate,
        status: activate ? "active" : "draft",
        decimal_precision: parseInt(decimalPrecision) || 2,
        fy_starts_on: fyStartsOn || "",
        fy_ends_on: fyEndsOn || "",
        reporting_mtd: reportingMtd,
        reporting_qtd: reportingQtd,
        reporting_ytd: reportingYtd,
        reporting_itd: reportingItd,
        commission_accounting_method: commissionAccountingMethod || "",
        date_format: dateFormat || "YYYY-MM-DD",
        timezone: timezone || "UTC",
        onboarding_mode: onboardingMode || "",
        multi_currency_enabled: multiCurrencyEnabled,
        fx_cost_method: fxCostMethod || "",
      };
      let effectiveFundId = fundId;
      if (!effectiveFundId) {
        // TASK 1: manual entry path — first persist happens here.
        const { data } = await API.post(
          "/funds",
          { org_id: selectedOrgId, name: info.name },
        );
        effectiveFundId = data.id;
        setFundId(effectiveFundId);
        setFundOrigin("manual");
      }
      await API.put(`/funds/${effectiveFundId}`, payload);
      await API.put(`/funds/${effectiveFundId}/workflow`, { required_fields: fields, required_documents: docs });
      // Explicit save completed → clear the guard so navigate() runs clean.
      setDirty(false);
      toast.success(activate ? "Fund activated" : "Fund saved as draft");
      navigate(`/super-admin/funds/${effectiveFundId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // TASK 1: TASK 1 unified handlers for the Save/Discard/Cancel modal.
  const guard = useUnsavedChangesGuard(dirty);

  const [saveDialogSaving, setSaveDialogSaving] = useState(false);

  const persistAsDraft = useCallback(async () => {
    // Minimum-payload draft save. Uses the same POST + PUT contract as
    // handleSave — DO NOT alter sync triggers or the response shape.
    setSaveDialogSaving(true);
    try {
      const classes = offerings.map(o => o.name).filter(Boolean);
      const payload = {
        ...info,
        investment_minimum: parseFloat(info.investment_minimum) || 0,
        reporting_frequency: reportingFreq || "",
        reporting_start_date: reportingStartDate || null,
        fund_category: fundCategory,
        class_type: offerings.length > 1 ? "multi" : "single",
        classes,
        offerings,
        shares_applicable: sharesApplicable,
        statement_template: statementTemplate,
        status: "draft",
        decimal_precision: parseInt(decimalPrecision) || 2,
        fy_starts_on: fyStartsOn || "",
        fy_ends_on: fyEndsOn || "",
        reporting_mtd: reportingMtd,
        reporting_qtd: reportingQtd,
        reporting_ytd: reportingYtd,
        reporting_itd: reportingItd,
        commission_accounting_method: commissionAccountingMethod || "",
        date_format: dateFormat || "YYYY-MM-DD",
        timezone: timezone || "UTC",
        onboarding_mode: onboardingMode || "",
        multi_currency_enabled: multiCurrencyEnabled,
        fx_cost_method: fxCostMethod || "",
      };
      let effectiveFundId = fundId;
      if (!effectiveFundId) {
        const { data } = await API.post("/funds", {
          org_id: selectedOrgId,
          name: info.name || "Untitled Fund",
        });
        effectiveFundId = data.id;
        setFundId(effectiveFundId);
        setFundOrigin("manual");
      }
      await API.put(`/funds/${effectiveFundId}`, payload);
      await API.put(`/funds/${effectiveFundId}/workflow`, { required_fields: fields, required_documents: docs });
      setDirty(false);
      toast.success("Saved as draft");
      guard.resumeAfterSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save draft");
    } finally {
      setSaveDialogSaving(false);
    }
  }, [
    info, offerings, reportingFreq, reportingStartDate, fundCategory,
    sharesApplicable, statementTemplate, decimalPrecision, fyStartsOn,
    fyEndsOn, reportingMtd, reportingQtd, reportingYtd, reportingItd,
    commissionAccountingMethod, dateFormat, timezone, onboardingMode,
    multiCurrencyEnabled, fxCostMethod, fields, docs, fundId, selectedOrgId, guard,
  ]);

  const discardChanges = useCallback(async () => {
    // Create-flow, PPM origin → delete the temp row so it doesn't linger.
    // Manual origin → nothing to delete (nothing was persisted).
    // Edit-existing-draft is not currently reachable from this page, but
    // if the parent ever wires it up we would revert to the last-saved
    // snapshot instead of deleting.
    if (fundOrigin === "ppm" && fundId) {
      try {
        await API.delete(`/funds/${fundId}`);
      } catch (err) {
        // Non-fatal: log and continue so the user can still leave. The
        // row can be manually cleaned up later.
        // eslint-disable-next-line no-console
        console.warn("Failed to delete temp PPM fund", err);
      }
    }
    setDirty(false);
    guard.resumeAfterDiscard();
  }, [fundOrigin, fundId, guard]);

  const handleBack = () => {
    guard.guardedNavigate(() => navigate("/super-admin/funds"));
  };

  // Wrap every state setter that flips content with a `setDirty(true)`
  // marker so we don't have to sprinkle it across the JSX. The trick:
  // mark dirty on the first user interaction inside the details section.
  const markDirty = useCallback(() => { if (!dirty) setDirty(true); }, [dirty]);
  // Show details section iff user picked a path: PPM upload finished OR
  // manual entry was started.
  const showDetails = !!fundId || manualMode;
  const memoizedGuard = useMemo(() => guard, [guard]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="create-fund-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={handleBack} data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Create Fund</h1>
          <p className="text-muted-foreground text-sm mt-1">Upload a PPM to auto-generate fund details with AI</p>
        </div>
      </div>

      {orgs.length === 0 && !showDetails && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-400 mb-2" data-testid="no-orgs-warning">
          Please create an <strong>Organization</strong> before adding Funds. Head over to{" "}
          <a href="/super-admin/organizations" className="underline font-medium">Organizations</a> to create one.
        </div>
      )}

      {/* Upload Section */}
      {!showDetails && (
        <Card data-testid="ppm-upload-card">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold">Create a new fund</h3>
                <p className="text-muted-foreground text-sm">Upload a PPM for AI-assisted setup, or fill in the form manually.</p>
              </div>
            </div>

            <div className="mb-6 space-y-2">
              <Label className="flex items-center gap-2 text-sm">
                <Network className="w-4 h-4 text-primary" />
                Organization *
              </Label>
              <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                <SelectTrigger
                  className={`bg-secondary/50 ${!selectedOrgId ? "border-amber-500/50" : ""}`}
                  data-testid="create-fund-org-select"
                >
                  <SelectValue placeholder="Select organization for this fund" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                  {orgs.length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No organizations. Create one first.</div>
                  )}
                </SelectContent>
              </Select>
              {!selectedOrgId && <p className="text-xs text-amber-400">Required — every fund must belong to an organization</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* PPM mode */}
              <div className="space-y-3 p-4 rounded-xl border border-border bg-secondary/10" data-testid="ppm-mode-panel">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <h4 className="text-sm font-semibold">AI-Assisted (PPM Upload)</h4>
                </div>
                <p className="text-xs text-muted-foreground">Upload the PPM PDF; we&apos;ll extract fund info, offerings, and onboarding requirements.</p>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  data-testid="ppm-dropzone"
                >
                  <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden" onChange={handleFileSelect} data-testid="ppm-file-input" />
                  {files.length > 0 ? (
                    <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                      {files.map((f, idx) => (
                        <div key={`${f.name}-${idx}`} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-secondary/30 border border-border/50">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                            <div className="text-left min-w-0">
                              <p className="font-medium text-xs truncate">{f.name}</p>
                              <p className="text-[10px] text-muted-foreground">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" className="w-6 h-6 flex-shrink-0 text-muted-foreground hover:text-red-400" onClick={() => removeFile(idx)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <button
                        className="w-full py-1.5 text-[10px] text-muted-foreground hover:text-primary border border-dashed border-border/50 rounded-lg transition-colors"
                        onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                      >
                        + Add more PDFs
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
                      <p className="text-xs text-muted-foreground">Drag & drop your PPM PDF(s)</p>
                      <p className="text-[10px] text-muted-foreground mt-1">or click to browse — multiple files supported</p>
                    </>
                  )}
                </div>
                <Button onClick={handleUpload} disabled={files.length === 0 || processing || !selectedOrgId} className="w-full h-10" data-testid="process-ppm-button">
                  {processing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating… ({files.length > 1 ? `${files.length} documents` : "document"})</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Process PPM{files.length > 1 ? ` (${files.length} files)` : ""}</>
                  )}
                </Button>
              </div>

              {/* Manual mode */}
              <div className="space-y-3 p-4 rounded-xl border border-border bg-secondary/10" data-testid="manual-mode-panel">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <h4 className="text-sm font-semibold">Manual Entry (Form)</h4>
                </div>
                <p className="text-xs text-muted-foreground">Fill in the fund details manually. Skip the PPM if you already have the information ready.</p>
                <div className="rounded-xl bg-card/40 border border-border/50 p-6 text-center">
                  <Plus className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-xs text-muted-foreground">Start with a blank fund</p>
                  <p className="text-[10px] text-muted-foreground mt-1">All fields editable below</p>
                </div>
                <Button
                  onClick={handleStartManual}
                  disabled={!selectedOrgId}
                  variant="secondary"
                  className="w-full h-10 border border-border"
                  data-testid="start-manual-fund-button"
                >
                  <Plus className="w-4 h-4 mr-2" /> Start Manual Entry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fund Details After PPM Parse */}
      {showDetails && (
        <>
          {fundOrigin === "ppm" && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-400">PPM analyzed successfully. Review and edit the extracted information below.</p>
              <Badge variant="secondary" className="ml-2 text-[10px] uppercase">{fundCategory === "open_ended" ? "Open Ended" : "Closed Ended"}</Badge>
            </div>
          )}
          {fundOrigin === "manual" && !fundId && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <FileText className="w-4 h-4 text-primary flex-shrink-0" />
              <p className="text-sm text-primary">Manual entry — nothing is saved until you click <strong>Save as Draft</strong> or <strong>Save & Activate</strong>.</p>
            </div>
          )}

          {/* Fund Info */}
          <Card data-testid="fund-info-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="font-heading text-lg">Fund Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fund Name *</Label>
                  <Input value={info.name} onChange={(e) => { setInfo({ ...info, name: e.target.value }); clearFieldError("name"); markDirty(); }} className={`bg-secondary/50 ${fieldErrors.name ? "border-red-500/60" : ""}`} data-testid="fund-name-input" />
                  {fieldErrors.name && <p className="text-xs text-red-400" data-testid="fund-name-error">{fieldErrors.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Fund Type</Label>
                  <Select value={info.fund_type} onValueChange={(v) => { handleFundTypeChange(v); markDirty(); }}>
                    <SelectTrigger className="bg-secondary/50" data-testid="fund-type-select"><SelectValue placeholder="Select fund type" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {FUND_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Auto-detected: <span className="font-medium">{fundCategory === "open_ended" ? "Open Ended" : "Closed Ended"}</span></p>
                </div>
                <div className="space-y-2">
                  <Label>Minimum Investment ($)</Label>
                  <Input type="number" value={info.investment_minimum} onChange={(e) => setInfo({ ...info, investment_minimum: e.target.value })} className="bg-secondary/50" data-testid="fund-min-invest-input" />
                </div>
                <div className="space-y-2">
                  <Label>Reporting Currency *</Label>
                  <Input value={info.currency} onChange={(e) => { setInfo({ ...info, currency: e.target.value }); clearFieldError("currency"); markDirty(); }} className={`bg-secondary/50 ${fieldErrors.currency ? "border-red-500/60" : ""}`} data-testid="fund-currency-input" />
                  {fieldErrors.currency && <p className="text-xs text-red-400" data-testid="fund-currency-error">{fieldErrors.currency}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Reporting Frequency *</Label>
                  <Select value={reportingFreq} onValueChange={setReportingFreq}>
                    <SelectTrigger className={`bg-secondary/50 ${!reportingFreq ? "border-amber-500/50" : ""}`} data-testid="reporting-freq-select">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                  {!reportingFreq && <p className="text-xs text-amber-400">Required — please select</p>}
                </div>
                <div className="space-y-2">
                  <Label>Reporting Start Date *</Label>
                  <Input type="date" value={reportingStartDate} onChange={e => { setReportingStartDate(e.target.value); clearFieldError("reportingStartDate"); markDirty(); }} className={`bg-secondary/50 ${fieldErrors.reportingStartDate ? "border-red-500/60" : ""}`} data-testid="reporting-start-date-input" />
                  {fieldErrors.reportingStartDate ? (
                    <p className="text-xs text-red-400" data-testid="reporting-start-date-error">{fieldErrors.reportingStartDate}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">The date from which reporting periods begin</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Investor Statement Template</Label>
                  <Select value={statementTemplate} onValueChange={setStatementTemplate}>
                    <SelectTrigger className="bg-secondary/50" data-testid="statement-template-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {STATEMENT_TEMPLATES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <span className="font-medium">{t.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{STATEMENT_TEMPLATES.find(t => t.value === statementTemplate)?.desc}</p>
                </div>
                <div className="space-y-2">
                  <Label>Share/Unit Tracking</Label>
                  <Button variant={sharesApplicable ? "default" : "secondary"} size="sm" className="h-8 text-xs border border-border" onClick={() => setSharesApplicable(!sharesApplicable)}>
                    {sharesApplicable ? "Enabled" : "Disabled"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Strategy</Label>
                <Textarea value={info.strategy} onChange={(e) => setInfo({ ...info, strategy: e.target.value })} className="bg-secondary/50 min-h-[80px]" data-testid="fund-strategy-input" />
              </div>
              <div className="space-y-2">
                <Label>Terms & Key Provisions</Label>
                <Textarea value={info.terms} onChange={(e) => setInfo({ ...info, terms: e.target.value })} className="bg-secondary/50 min-h-[80px]" data-testid="fund-terms-input" />
              </div>
            </CardContent>
          </Card>

          {/* Fund Profile (extended details) */}
          <Card data-testid="fund-profile-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="font-heading text-lg">Fund Profile</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Additional fund identification and registration details</p>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              <div className="space-y-2">
                <Label>Fund Description</Label>
                <Textarea
                  value={info.fund_description}
                  onChange={(e) => setInfo({ ...info, fund_description: e.target.value })}
                  className="bg-secondary/50 min-h-[80px]"
                  placeholder="Brief description of the fund"
                  data-testid="fund-description-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fund Address</Label>
                  <Textarea
                    value={info.fund_address}
                    onChange={(e) => setInfo({ ...info, fund_address: e.target.value })}
                    className="bg-secondary/50 min-h-[60px]"
                    placeholder="Registered office address"
                    data-testid="fund-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Incorporation Date *</Label>
                  <Input
                    type="date"
                    value={info.incorp_date}
                    onChange={(e) => { setInfo({ ...info, incorp_date: e.target.value }); clearFieldError("incorp_date"); markDirty(); }}
                    className={`bg-secondary/50 ${fieldErrors.incorp_date ? "border-red-500/60" : ""}`}
                    data-testid="fund-incorp-date-input"
                  />
                  {fieldErrors.incorp_date && <p className="text-xs text-red-400" data-testid="fund-incorp-date-error">{fieldErrors.incorp_date}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reporting & Operations */}
          <Card data-testid="fund-operations-card">
            <CardHeader className="p-6 pb-3">
              <CardTitle className="font-heading text-lg">Reporting & Operations</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Fiscal year, formatting, currency, and accounting configuration</p>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>FY Starts On</Label>
                  <Input
                    type="date"
                    value={fyStartsOn}
                    onChange={(e) => setFyStartsOn(e.target.value)}
                    className="bg-secondary/50"
                    data-testid="fy-starts-on-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>FY Ends On</Label>
                  <Input
                    type="date"
                    value={fyEndsOn}
                    onChange={(e) => setFyEndsOn(e.target.value)}
                    className="bg-secondary/50"
                    data-testid="fy-ends-on-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Decimal Precision</Label>
                  <Select value={String(decimalPrecision)} onValueChange={(v) => setDecimalPrecision(parseInt(v))}>
                    <SelectTrigger className="bg-secondary/50" data-testid="decimal-precision-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {[0, 1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select value={dateFormat} onValueChange={setDateFormat}>
                    <SelectTrigger className="bg-secondary/50" data-testid="date-format-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="DD-MMM-YYYY">DD-MMM-YYYY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger className="bg-secondary/50" data-testid="timezone-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">America/New_York</SelectItem>
                      <SelectItem value="Europe/London">Europe/London</SelectItem>
                      <SelectItem value="Asia/Dubai">Asia/Dubai</SelectItem>
                      <SelectItem value="Asia/Kolkata">Asia/Kolkata</SelectItem>
                      <SelectItem value="Asia/Singapore">Asia/Singapore</SelectItem>
                      <SelectItem value="Asia/Hong_Kong">Asia/Hong_Kong</SelectItem>
                      <SelectItem value="Asia/Tokyo">Asia/Tokyo</SelectItem>
                      <SelectItem value="Australia/Sydney">Australia/Sydney</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Onboarding Mode <span className="text-destructive">*</span></Label>
                  <Select value={onboardingMode} onValueChange={(v) => { setOnboardingMode(v); clearFieldError("onboardingMode"); markDirty(); }}>
                    <SelectTrigger className={`bg-secondary/50 ${fieldErrors.onboardingMode ? "border-red-500/60" : ""}`} data-testid="onboarding-mode-select">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="new" data-testid="onboarding-mode-option-new">New Fund</SelectItem>
                      <SelectItem value="existing" data-testid="onboarding-mode-option-existing">Existing Fund</SelectItem>
                    </SelectContent>
                  </Select>
                  {fieldErrors.onboardingMode && <p className="text-xs text-red-400" data-testid="onboarding-mode-error">{fieldErrors.onboardingMode}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Commission Accounting Method</Label>
                  <Select value={commissionAccountingMethod} onValueChange={setCommissionAccountingMethod}>
                    <SelectTrigger className="bg-secondary/50" data-testid="commission-accounting-method-select">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="CAPITAL">Capital</SelectItem>
                      <SelectItem value="EXPENSE">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>FX Cost Method</Label>
                  <Select value={fxCostMethod} onValueChange={setFxCostMethod}>
                    <SelectTrigger className="bg-secondary/50" data-testid="fx-cost-method-select">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="FIFO">FIFO</SelectItem>
                      <SelectItem value="LIFO">LIFO</SelectItem>
                      <SelectItem value="Average">Average</SelectItem>
                      <SelectItem value="WAC">Weighted Average Cost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Multi-Currency Enabled</Label>
                    <p className="text-xs text-muted-foreground">Allow this fund to operate in multiple currencies</p>
                  </div>
                  <Switch
                    checked={multiCurrencyEnabled}
                    onCheckedChange={setMultiCurrencyEnabled}
                    data-testid="multi-currency-enabled-switch"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Reporting Periods</Label>
                  <p className="text-xs text-muted-foreground">Select which reporting periods are enabled for this fund</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/30">
                    <Label className="text-xs">MTD</Label>
                    <Switch checked={reportingMtd} onCheckedChange={setReportingMtd} data-testid="reporting-mtd-switch" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/30">
                    <Label className="text-xs">QTD</Label>
                    <Switch checked={reportingQtd} onCheckedChange={setReportingQtd} data-testid="reporting-qtd-switch" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/30">
                    <Label className="text-xs">YTD</Label>
                    <Switch checked={reportingYtd} onCheckedChange={setReportingYtd} data-testid="reporting-ytd-switch" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/30">
                    <Label className="text-xs">ITD</Label>
                    <Switch checked={reportingItd} onCheckedChange={setReportingItd} data-testid="reporting-itd-switch" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Offerings */}
          <Card data-testid="offerings-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg">Fund Offerings</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">Offering classes extracted from PPM — review and edit fee structures</p>
                </div>
                <Button variant="secondary" size="sm" className="border border-border" onClick={addOffering} data-testid="add-offering-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Offering
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {offerings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No offerings detected. Add at least one offering for this fund.
                </div>
              ) : (
                <div className="space-y-4">
                  {offerings.map((off, idx) => (
                    <Card key={off.id || idx} className="border border-border/50 bg-secondary/10" data-testid={`offering-${idx}`}>
                      <CardContent className="p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold">{off.name || `Offering ${idx + 1}`}</h4>
                          <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeOffering(idx)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Offering Name</Label>
                            <Input value={off.name} onChange={e => updateOffering(idx, "name", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="e.g. Class A" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Type</Label>
                            <Select value={off.type} onValueChange={v => updateOffering(idx, "type", v)}>
                              <SelectTrigger className="bg-secondary/50 h-8 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-card border-border">{OFFERING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Management Fee (%)</Label>
                            <Input value={off.management_fee} onChange={e => updateOffering(idx, "management_fee", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="e.g. 2" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Performance Fee (%)</Label>
                            <Input value={off.performance_fee} onChange={e => updateOffering(idx, "performance_fee", e.target.value)} className="bg-secondary/50 h-8 text-sm" placeholder="e.g. 20" />
                          </div>
                        </div>
                        {/* Drivers */}
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Drivers</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1"><Label className="text-xs">MF Logic</Label><Textarea value={off.mf_logic} onChange={e => updateOffering(idx, "mf_logic", e.target.value)} className="bg-secondary/50 text-sm min-h-[60px]" placeholder="Management fee calculation logic..." /></div>
                            <div className="space-y-1"><Label className="text-xs">PF Logic</Label><Textarea value={off.pf_logic} onChange={e => updateOffering(idx, "pf_logic", e.target.value)} className="bg-secondary/50 text-sm min-h-[60px]" placeholder="Performance fee calculation logic..." /></div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div className="space-y-1"><Label className="text-xs">Hurdle Rate (%)</Label><Input value={off.hurdle_rate} onChange={e => updateOffering(idx, "hurdle_rate", e.target.value)} className="bg-secondary/50 h-8 text-sm" /></div>
                            <div className="space-y-1"><Label className="text-xs">Hurdle Logic</Label><Input value={off.hurdle_logic} onChange={e => updateOffering(idx, "hurdle_logic", e.target.value)} className="bg-secondary/50 h-8 text-sm" /></div>
                          </div>
                        </div>
                        {/* Terms & Conditions — Applicability Toggles */}
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Terms & Conditions</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                              { key: "high_watermark", label: "High Watermark", toggle: "high_watermark_applicable" },
                              { key: "loss_carry_forward", label: "Loss Carry Forward", toggle: "loss_carry_forward_applicable" },
                              { key: "clawback", label: "Clawback", toggle: "clawback_applicable" },
                              { key: "others", label: "Others", toggle: "others_applicable" },
                            ].map(term => (
                              <div key={term.key} className="space-y-2 p-3 rounded-lg bg-secondary/20 border border-border/30">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs font-semibold">{term.label}</Label>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-muted-foreground">{off[term.toggle] ? "Applicable" : "N/A"}</span>
                                    <Switch checked={!!off[term.toggle]} onCheckedChange={v => updateOffering(idx, term.toggle, v)} />
                                  </div>
                                </div>
                                {off[term.toggle] && (
                                  <Textarea value={off[term.key]} onChange={e => updateOffering(idx, term.key, e.target.value)} className="bg-secondary/50 text-sm min-h-[50px]" placeholder={`${term.label} details...`} />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Onboarding Workflow — Fields */}
          <Card data-testid="workflow-fields-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Required Investor Fields</CardTitle>
                <Button variant="secondary" size="sm" onClick={addField} className="border border-border" data-testid="add-field-button">
                  <Plus className="w-4 h-4 mr-1" /> Add Field
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              {fields.map((field, i) => (
                <div key={field.id || i} className="flex flex-col gap-2 p-3 rounded-lg bg-secondary/30 border border-border/50" data-testid={`workflow-field-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input placeholder="Field name" value={field.field_name} onChange={(e) => updateField(i, "field_name", e.target.value)} className="bg-secondary/50 text-sm" />
                      <Select value={field.field_type} onValueChange={(v) => updateField(i, "field_type", v)}>
                        <SelectTrigger className="bg-secondary/50 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {field.field_type === "select" && (
                        <Input placeholder="Options (comma-separated)" value={(field.options || []).join(", ")} onChange={(e) => updateField(i, "options", e.target.value.split(",").map((s) => s.trim()))} className="bg-secondary/50 text-sm" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <div className="flex items-center gap-1.5">
                        <Switch checked={field.required} onCheckedChange={(v) => updateField(i, "required", v)} />
                        <span className="text-xs text-muted-foreground">Req</span>
                      </div>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeField(i)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="pl-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Applies To</p>
                    <InvestorTypeSelector
                      value={field.applicable_investor_types || field.applicable_entity_types || []}
                      onChange={(next) => updateField(i, "applicable_investor_types", next)}
                      testId={`cf-field-types-${i}`}
                    />
                  </div>
                </div>
              ))}
              {fields.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No fields defined yet</p>}
            </CardContent>
          </Card>

          {/* Onboarding Workflow — Documents */}
          <Card data-testid="workflow-docs-card">
            <CardHeader className="p-6 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Required Documents</CardTitle>
                <Button variant="secondary" size="sm" onClick={addDoc} className="border border-border" data-testid="add-doc-button">
                  <Plus className="w-4 h-4 mr-1" /> Add Document
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              {docs.map((doc, i) => (
                <div key={doc.id || i} className="flex flex-col gap-2 p-3 rounded-lg bg-secondary/30 border border-border/50" data-testid={`workflow-doc-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input placeholder="Document name" value={doc.doc_name} onChange={(e) => updateDoc(i, "doc_name", e.target.value)} className="bg-secondary/50 text-sm" />
                      <Input placeholder="Description" value={doc.description || ""} onChange={(e) => updateDoc(i, "description", e.target.value)} className="bg-secondary/50 text-sm" />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <div className="flex items-center gap-1.5">
                        <Switch checked={doc.required} onCheckedChange={(v) => updateDoc(i, "required", v)} />
                        <span className="text-xs text-muted-foreground">Req</span>
                      </div>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-red-400" onClick={() => removeDoc(i)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="pl-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Applies To</p>
                    <InvestorTypeSelector
                      value={doc.applicable_investor_types || doc.applicable_entity_types || []}
                      onChange={(next) => updateDoc(i, "applicable_investor_types", next)}
                      testId={`cf-doc-types-${i}`}
                    />
                  </div>
                </div>
              ))}
              {docs.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No documents defined yet</p>}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" className="border border-border" onClick={() => handleSave(false)} disabled={saving} data-testid="save-draft-button">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save as Draft"}
            </Button>
            <Button onClick={() => handleSave(true)} disabled={saving} className="shadow-[0_0_20px_rgba(108,71,196,0.2)]" data-testid="save-activate-button">
              <CheckCircle2 className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save & Activate"}
            </Button>
          </div>
        </>
      )}

      <UnsavedChangesDialog
        open={memoizedGuard.promptOpen}
        onOpenChange={(v) => { if (!v) memoizedGuard.cancel(); }}
        onSaveDraft={persistAsDraft}
        onDiscard={discardChanges}
        onCancel={memoizedGuard.cancel}
        saving={saveDialogSaving}
      />
    </div>
  );
}
